import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import type { Instance, StandaloneConfiguration } from '@nutrient-sdk/viewer';
import { customiseInlineToolbar } from './customiseInlineToolbar';
import { customiseToolbar, ToolbarItemType } from './customiseToolbar';
import { hideExistingAnnotations } from './hideExistingAnnotations';
import { searchWithFallback } from './searchWithFallback';
import { Document } from 'types';

export type UseDocumentViewerConfig = {
  licenseKey: string;
  inlineToolbarItems?: (instance: Instance) => unknown[];
  styleSheets?: string[];
};

export type UseDocumentViewerResult = {
  setSelectedDocument: (document: Document | null) => void;
  selectedDocument: Document | null;
  containerRef: (node: HTMLDivElement | null) => void;
  navigateToPage: (pageIndex: number) => void;
  searchDocument: (term: string) => Promise<number>;
  clearSearch: () => void;
  isLoading: boolean;
  isDocumentRendered: boolean;
  error: Error | null;
};

// Toolbar items rendered in this order
const defaultToolbarItems = [
  'sidebar-thumbnails',
  'sidebar-document-outline',
  'pager',
  'pan',
  'zoom-out',
  'zoom-in',
  'zoom-mode',
  'search',
] as ToolbarItemType[];

const emptyToolbar = () => [];

/**
 * Hook to load and interact with the Nutrient Document Viewer.
 * Isolates the provider implementation details.
 */
export function useDocumentViewer({
  licenseKey,
  inlineToolbarItems = emptyToolbar,
  styleSheets = [],
}: UseDocumentViewerConfig): UseDocumentViewerResult {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  );
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const viewerInstanceRef = useRef<Instance | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDocumentRendered, setIsDocumentRendered] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);
  const customStylesheets = useMemo(() => ['/pdf-theme.css', ...styleSheets], [
    styleSheets,
  ]);

  useEffect(() => {
    if (!container) return;

    container.setAttribute('data-pdf-rendered', String(isDocumentRendered));
  }, [container, isDocumentRendered]);

  // Start the WebAssembly worker while the document list loads, load() reuses it
  useEffect(() => {
    import('@nutrient-sdk/viewer')
      .then(({ default: NutrientViewer }) =>
        // The type requires a document, but preloading doesn't use one
        NutrientViewer.preloadWorker({
          licenseKey,
          useCDN: true,
        } as StandaloneConfiguration)
      )
      .catch(() => {
        // Safe to ignore, preloading is only a speed up. load() fetches the worker itself and reports errors
      });
  }, [licenseKey]);

  useEffect(() => {
    let NutrientViewer: typeof import('@nutrient-sdk/viewer').default;

    if (!container || !selectedDocument) return;

    const loadViewer = async () => {
      viewerInstanceRef.current = undefined;
      setIsLoading(true);
      setIsDocumentRendered(false);
      setError(null);
      try {
        // Lazy load NutrientViewer library
        const module = await import('@nutrient-sdk/viewer');
        NutrientViewer = module.default;
        // Ensure there’s only one `NutrientViewer` instance.
        NutrientViewer.unload(container);

        // Ignore document permissions to allow users to create annotations, this does not modify the document at all
        // By default the viewer will not enable certain features if document permissions are set
        // Options are frozen the first time NutrientViewer.load is called, and won't be modifiable after that
        if (!NutrientViewer.Options.IGNORE_DOCUMENT_PERMISSIONS) {
          NutrientViewer.Options.IGNORE_DOCUMENT_PERMISSIONS = true;
        }

        const instance = await NutrientViewer.load({
          container, // Container where PSPDFKit will be mounted
          document: selectedDocument.url, // remote url of document (or, filename if under /public)
          licenseKey: licenseKey,
          styleSheets: customStylesheets, // Default to stylesheet for increasing UI font sizes
          locale: 'en', // force locale to english as we remove non english locales as part of the asset cleaner
          useCDN: true, // Load assets from CDN
          toolbarItems: [], // Start with empty toolbar and customise after load
          ui: { loader: { type: 'progress' } },
          renderPageCallback: function (ctx, pageIndex) {
            // Wait for first page to load, the rest are lazy loaded - http://nutrient.io/guides/web/knowledge-base/detect-pspdfkit-ui-loaded/
            if (pageIndex === 0) {
              setIsDocumentRendered(true);
            }
          },
        });
        viewerInstanceRef.current = instance;
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to load Document viewer')
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadViewer();

    return () => {
      if (NutrientViewer && container) {
        NutrientViewer.unload(container);
      }
      viewerInstanceRef.current = undefined;
    };
  }, [selectedDocument, licenseKey, container]);

  const navigateToPage = useCallback((pageIndex: number) => {
    viewerInstanceRef.current?.setViewState((viewState: any) =>
      viewState.set('currentPageIndex', pageIndex)
    );
  }, []);
  useEffect(() => {
    if (!viewerInstanceRef.current) return;

    customiseToolbar(viewerInstanceRef.current, defaultToolbarItems);
    customiseInlineToolbar(viewerInstanceRef.current, inlineToolbarItems);
    hideExistingAnnotations(viewerInstanceRef.current);
  }, [viewerInstanceRef.current]);

  const searchDocument = useCallback(async (term: string) => {
    const instance = viewerInstanceRef.current;
    if (!instance) return 0;
    return searchWithFallback(instance, term);
  }, []);

  const clearSearch = useCallback(() => {
    viewerInstanceRef.current?.setSearchState((searchState: any) =>
      searchState.set('term', '').set('results', searchState.results.clear())
    );
    viewerInstanceRef.current?.setViewState((viewState: any) =>
      viewState.set('interactionMode', null)
    );
  }, []);

  return {
    setSelectedDocument,
    selectedDocument,
    containerRef,
    navigateToPage,
    searchDocument,
    clearSearch,
    isLoading,
    isDocumentRendered,
    error,
  };
}
